import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { OrderOutModel } from '../../models/order-out-model/order-out-model';
import { environment } from 'src/environments/environment';
import { tap, catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { toCanonicalSparePartKeys } from 'src/app/utils/spare-part-payload.util';
import { parseWallClockDateTimeFromApi } from 'src/app/utils/wall-clock-datetime.util';
// Interfaz simplificada para actualizar órdenes (basada en OrderModel del backend)
interface UpdateOrderModel {
  orderId: number;
  providerName: string;
  providerPhone: string;
  clientName: string;
  clientPhone: string;
  invoiceNumber: string;
  employeeName: string;
  siniestro: string;
  deliveryAddress: string;
  carMakeModel: string;
  isExport: string;
  orderStatus: string;
  sendYesOrNo: string;
  orderNumber: string;
  isNearArrival: boolean;
  isNearArrivalAccepted: boolean;
  clienteUY: string;
  workshopId?: number;
  expectedArrivalDate: string;
  emergentComment?: string;
}

interface PaginatedResponse {
  orders: OrderOutModel[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

interface DistinctValuesResponse {
  values: string[];
  hasEmpty: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OrdersService {

  private apiUrl = environment.apiEndpoint + '/order';

  constructor(
    private http: HttpClient
  ) { }

  getOrders(): Observable<OrderOutModel[]> {
    return this.http.get<OrderOutModel[]>(this.apiUrl);
  }

  deleteOrder(orderId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${orderId}`);
  }

  getPaginatedOrders(
    page: number, 
    pageSize: number, 
    searchTerm?: string,
    startDate?: Date,
    endDate?: Date,
    productStatus?: string,
    workshopId?: number | null,
    maxAgeDays?: number | null,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    columnFilters?: Record<string, string[]>
  ): Observable<PaginatedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    if (productStatus) {
      params = params.set('productStatus', productStatus);
    }
    if (workshopId !== null && workshopId !== undefined) {
      params = params.set('workshopId', workshopId.toString());
    }
    if (maxAgeDays != null && maxAgeDays > 0) {
      params = params.set('maxAgeDays', maxAgeDays.toString());
    }
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      params = params.set('columnFilters', JSON.stringify(columnFilters));
    }

    return this.http.get<PaginatedResponse>(`${this.apiUrl}/paginated`, { params })
      .pipe(
        catchError(error => {
          console.error('Error al obtener órdenes:', error);
          return throwError(() => error);
        })
      );
  }

  getBasicOrderInfo(): Observable<any> {
    return this.http.get(`${environment.apiEndpoint}/order/basic`);
  }

  getOrdersByIds(orderIds: number[]): Observable<OrderOutModel[]> {
    const params = new HttpParams().set('ids', orderIds.join(','));
    return this.http.get<OrderOutModel[]>(`${this.apiUrl}/byIds`, { params });
  }

  // Método para obtener todas las órdenes que coincidan con los filtros
  getAllFilteredOrders(
    searchTerm?: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<OrderOutModel[]> {
    let params = new HttpParams();

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }

    return this.http.get<OrderOutModel[]>(`${this.apiUrl}/all-filtered`, { params })
      .pipe(
        catchError(error => {
          console.error('Error al obtener todas las órdenes filtradas:', error);
          return throwError(() => error);
        })
      );
  }

  getDistinctColumnValues(
    column: string,
    searchTerm?: string,
    startDate?: Date,
    endDate?: Date,
    workshopId?: number | null,
    columnFilters?: Record<string, string[]>,
    maxAgeDays?: number | null
  ): Observable<DistinctValuesResponse> {
    let params = new HttpParams().set('column', column);

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    if (workshopId !== null && workshopId !== undefined) {
      params = params.set('workshopId', workshopId.toString());
    }
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      params = params.set('columnFilters', JSON.stringify(columnFilters));
    }
    if (maxAgeDays != null && maxAgeDays > 0) {
      params = params.set('maxAgeDays', maxAgeDays.toString());
    }

    return this.http.get<DistinctValuesResponse>(`${this.apiUrl}/distinct-values`, { params }).pipe(
      catchError(error => {
        console.error('Error al obtener valores únicos de columna (orders):', error);
        return throwError(() => error);
      })
    );
  }

  // Método para obtener el conteo total de órdenes
  getTotalOrdersCount(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/count`).pipe(
      catchError(error => {
        console.error('Error al obtener el conteo de órdenes:', error);
        return throwError(() => error);
      })
    );
  }



  // Método para convertir OrderOutModel a UpdateOrderModel
  private convertToUpdateOrderModel(order: OrderOutModel): UpdateOrderModel {
    return {
      orderId: order.orderId,
      providerName: '', // No disponible en OrderOutModel, se puede agregar después
      providerPhone: '', // No disponible en OrderOutModel, se puede agregar después
      clientName: order.clientName,
      clientPhone: order.clientPhone,
      invoiceNumber: order.invoiceNumber,
      employeeName: order.employeeName,
      siniestro: order.siniestro,
      deliveryAddress: order.deliveryAddress,
      carMakeModel: order.carMakeModel,
      isExport: order.isExport,
      orderStatus: order.orderStatus,
      sendYesOrNo: order.sendYesOrNo,
      orderNumber: order.orderNumber,
      isNearArrival: order.isNearArrival,
      isNearArrivalAccepted: order.isNearArrivalAccepted,
      clienteUY: order.clienteUY,
      workshopId: order.workshopId,
      expectedArrivalDate: order.expectedArrivalDate,
      emergentComment: order.emergentComment
    };
  }

  // Método para actualizar una orden completa
  updateOrder(orderId: number, order: OrderOutModel): Observable<any> {
    const updateData = this.convertToUpdateOrderModel(order);
    return this.http.put<any>(`${this.apiUrl}/${orderId}`, updateData).pipe(
      catchError(error => {
        console.error('Error al actualizar la orden:', error);
        return throwError(() => error);
      })
    );
  }

  // Método para obtener una orden específica por ID
  getOrderById(orderId: number): Observable<OrderOutModel> {
    return this.http.get<any>(`${this.apiUrl}/${orderId}`).pipe(
      map((response: any) => {
        // CRÍTICO: Mapear la estructura anidada del backend a la estructura plana del frontend
        if (response.spareParts && Array.isArray(response.spareParts)) {
          response.spareParts = response.spareParts.map((sp: any) => {
            const source = sp.SparePart ? sp.SparePart : sp;
            const canonical = toCanonicalSparePartKeys(source);
            const remito = (canonical.shippingReceipt || '').trim();

            return {
              ...canonical,
              shippingReceipt: remito,
              orderId: canonical.orderId ?? response.orderId ?? response.OrderId,
              orderNumber: canonical.orderNumber || response.orderNumber || response.OrderNumber || '',
              lastExportedAt: canonical.lastExportedAt != null
                ? new Date(canonical.lastExportedAt as string | Date)
                : undefined,
              arrivalDate: canonical.arrivalDate != null
                ? parseWallClockDateTimeFromApi(canonical.arrivalDate as string | Date)
                : undefined,
              fechaExportacion: canonical.fechaExportacion != null
                ? new Date(canonical.fechaExportacion as string | Date)
                : undefined,
              expectedArrivalDate: canonical.expectedArrivalDate != null
                ? new Date(canonical.expectedArrivalDate as string | Date)
                : undefined,
              fechaDeComunicado: canonical.fechaDeComunicado != null
                ? new Date(canonical.fechaDeComunicado as string | Date)
                : undefined,
              mlDeadline: canonical.mlDeadline != null
                ? new Date(canonical.mlDeadline as string | Date)
                : undefined
            };
          });
        }
        return response as OrderOutModel;
      }),
      catchError(error => {
        console.error('Error al obtener la orden por ID:', error);
        return throwError(() => error);
      })
    );
  }

  // Método para obtener una orden específica por número
  getOrderByOrderNumber(orderNumber: string): Observable<OrderOutModel> {
    return this.http.get<OrderOutModel>(`${this.apiUrl}/by-order-number`, {
      params: { orderNumber }
    }).pipe(
      catchError(error => {
        console.error('Error al obtener la orden por número:', error);
        return throwError(() => error);
      })
    );
  }

  // Método para obtener el siguiente número de paquete disponible
  getNextPackageNumber(exportNumber: string): Observable<{ nextPackageNumber: string; exportNumber: string }> {
    const enc = encodeURIComponent(exportNumber);
    return this.http.get<{ nextPackageNumber: string; exportNumber: string }>(`${this.apiUrl}/next-package-number/${enc}`).pipe(
      catchError(error => {
        console.error('Error al obtener el siguiente número de paquete:', error);
        return throwError(() => error);
      })
    );
  }
}
